import { Controller } from "@hotwired/stimulus";
import { composerMayTakeFocus } from "../lib/composerFocus.mjs";

export default class extends Controller {
  static targets = [
    "input",
    "fileInput",
    "previews",
    "wrapper",
    "sendButton",
    "form",
  ];

  connect() {
    if (this.hasInputTarget) this.resize();
  }

  // The composer has no `autofocus` (see lib/composerFocus.mjs for why): it
  // focuses itself here, on first render and each time a send swaps in a new
  // form, but only when the customer isn't already typing somewhere else.
  inputTargetConnected(input) {
    const panel = input.closest(".kit-chat-panel");
    const focusable = composerMayTakeFocus({
      activeElement: document.activeElement,
      body: document.body,
      insideClosedPanel:
        !!panel && !panel.classList.contains("kit-chat-panel--open"),
    });
    if (focusable) input.focus({ preventScroll: true });
  }

  // Sending a message replaces the whole form (messages/create.turbo_stream.erb
  // replaces #new_message_<chat_id>), and the replacement is server-rendered with
  // the hidden page fields blank. This controller's own element wraps the form
  // and is NOT replaced, so connect() does not run again — populating there meant
  // only the FIRST message of a session ever carried a URL, and every message
  // after it reached Kit with page_url="". Kit then had to ask the customer which
  // screen they were on, and spend a screenshot round-trip to find out.
  //
  // formTargetConnected fires for each new form Turbo swaps in, so every message
  // is stamped with the page it was actually sent from. The submit-time refresh
  // is declared as a data-action on the form itself (see messages/_form) rather
  // than bound here, so it survives the same replacement.
  formTargetConnected() {
    // A replaced form is a fresh one: whatever was in flight is over.
    this.submitting = false;
    this.populatePageContext();
  }

  // One submission at a time, per form.
  //
  // Chat 679 on 2026-07-26: one keypress produced two Turbo form submissions 6ms
  // apart, so the customer's message was stored twice and answered twice by two
  // concurrent agent turns — which then interleaved into a history the provider
  // rejects, bricking their conversation permanently. Two Enter keydowns inside
  // the 50ms before clearAfterSubmit() empties the textarea both see text and
  // both submit; #keydown alone can't see the second one coming.
  //
  // Guarding the form's own `submit` event rather than the keypress is what
  // makes this cover every way a message can be sent: Enter, a double-click on
  // the send button (a native submit, which no requestSubmit guard would ever
  // see), a suggestion chip, and the interactive Kit blocks — they all end here.
  //
  // Turbo reads `defaultPrevented` on the bubbled event before deciding to
  // submit, so preventing it here stops the request being made at all.
  guardSubmit(event) {
    if (this.submitting) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    this.submitting = true;
  }

  // Fires on success AND on failure, so a request that errors leaves the form
  // usable. The customer must never be locked out of their own chat by a guard.
  submitEnded() {
    this.submitting = false;
  }

  // Auto-resize textarea
  resize() {
    const input = this.inputTarget;
    input.style.height = "auto";
    input.style.height = Math.min(input.scrollHeight, 150) + "px";
  }

  // Submit on Enter (Shift+Enter for newline)
  keydown(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      if (
        this.inputTarget.value.trim() ||
        this.fileInputTarget.files.length > 0
      ) {
        this.formTarget.requestSubmit();
        this.clearAfterSubmit();
      }
    }
  }

  // Populate hidden fields with current page context before submission
  populatePageContext() {
    const form = this.formTarget;
    let urlField = form.querySelector("input[name='message[page_url]']");
    let titleField = form.querySelector("input[name='message[page_title]']");

    if (urlField) {
      urlField.value = window.location.pathname + window.location.search;
    }
    if (titleField) {
      titleField.value = document.title;
    }
  }

  // Trigger hidden file input
  triggerFileSelect() {
    this.fileInputTarget.click();
  }

  // Handle file selection
  filesSelected() {
    this.renderPreviews(this.fileInputTarget.files);
  }

  // Drag and drop — works on the whole chat area
  dragover(event) {
    event.preventDefault();
    this.element.classList.add("kit-drag-over");
  }

  dragleave(event) {
    // Only remove if leaving the controller element entirely
    if (!this.element.contains(event.relatedTarget)) {
      this.element.classList.remove("kit-drag-over");
    }
  }

  drop(event) {
    event.preventDefault();
    this.element.classList.remove("kit-drag-over");

    const files = event.dataTransfer.files;
    if (files.length > 0) {
      const dt = new DataTransfer();
      for (const file of files) {
        dt.items.add(file);
      }
      this.fileInputTarget.files = dt.files;
      this.renderPreviews(dt.files);
      // Scroll to bottom to show previews
      this.inputTarget.focus();
    }
  }

  // Render file previews
  renderPreviews(files) {
    const container = this.previewsTarget;
    container.innerHTML = "";

    for (const file of files) {
      const preview = document.createElement("div");
      preview.className = "kit-file-preview-item";

      if (file.type.startsWith("image/")) {
        const img = document.createElement("img");
        img.src = URL.createObjectURL(file);
        img.onload = () => URL.revokeObjectURL(img.src);
        preview.appendChild(img);
      } else {
        const icon = document.createElement("i");
        icon.className = "file outline icon";
        preview.appendChild(icon);
      }

      const name = document.createElement("span");
      name.className = "kit-file-preview-name";
      name.textContent = file.name;
      preview.appendChild(name);

      const remove = document.createElement("button");
      remove.type = "button";
      remove.className = "kit-file-preview-remove";
      remove.innerHTML = '<i class="close icon"></i>';
      remove.addEventListener("click", () => {
        preview.remove();
        this.removeFile(file);
      });
      preview.appendChild(remove);

      container.appendChild(preview);
    }
  }

  // Remove a file from the input
  removeFile(fileToRemove) {
    const dt = new DataTransfer();
    const files = this.fileInputTarget.files;
    for (let i = 0; i < files.length; i++) {
      if (files[i] !== fileToRemove) {
        dt.items.add(files[i]);
      }
    }
    this.fileInputTarget.files = dt.files;

    if (dt.files.length === 0) {
      this.previewsTarget.innerHTML = "";
    }
  }

  clearAfterSubmit() {
    setTimeout(() => {
      this.inputTarget.value = "";
      this.inputTarget.style.height = "auto";
      this.previewsTarget.innerHTML = "";
    }, 50);
  }
}
